import {
  ANCHORS,
  MISSING_FACTS,
  gapsInWhatIsKnown,
  isLabelFact,
  sparkles,
  stillToRead,
  walkedFor,
  whatIsMissing,
  type LabelFact,
  type MissingFact,
  type UpdateWineRequest,
  type WineGap,
  type WineListRow,
  type WineSummary,
} from "@corklog/shared";
import { decimalOf, formatDecimal, soundDecimal } from "../decimal.js";
import { useCallback, useId, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router";

import {
  ApiError,
  fetchWines,
  setLabelSilence,
  updateWine,
  uploadWinePhoto,
} from "../api.js";
import { useMayWrite } from "../cellar.js";
import { CellarHeading } from "../components/CellarHeading.js";
import { OwnersOnly } from "../components/OwnersOnly.js";
import { actionButton, control, fieldLabel, resting } from "../components/controls.js";
import { useReload } from "../offline.js";
import { resizeForUpload, UnreadableImage } from "../photos.js";

/**
 * What the cellar does not know about its own bottles, laid out as a walk
 * through the cabinets.
 *
 * This is the screen for task `8.19`, and `8.19` is data entry rather than
 * code: 29 Champagnes whose disgorgement dates are printed on back labels in a
 * basement, 11 non-vintage ones whose base year is not recorded anywhere, and
 * 11 wines carrying neither a year nor a flag saying they have none. What is
 * built here is the *order* that work is done in. Reading the back labels is
 * one trip past the cabinets; finding each of those wines in the list and
 * opening its form is fifty-one trips through the interface, and the second
 * cost is the one that stops it being done at all.
 *
 * So the list is grouped by where the bottles physically stand, deepest fact
 * first: open a door, and everything this screen wants from that shelf is in
 * front of you. It is the same claim the wine list's left gutter makes — the
 * eye scanning the screen and the hand scanning the shelf are doing the same
 * job in the same order.
 *
 * **The walk is asked what it is walking for.** That is `10.9`, and it came
 * back from the cabinets rather than from an argument: the list is the right
 * list and its membership rule was too narrow. On one evening the errand is
 * the disgorgement dates, on the next it is the alcohol off thirty back
 * labels, and on the one after that it is photographing bottles nobody has
 * ever photographed. So the reader ticks what should summon a wine, and the
 * anchors stay ticked by default — opening this screen shows exactly what it
 * showed before, because a screen that opened on 122 rows is the list of work
 * that stops being read.
 *
 * **No route of its own.** A row saves through the ordinary wine update with
 * everything else sent back exactly as it arrived, which is safe for one
 * specific reason: the route keeps a drinking-window year's provenance when
 * the year has not changed (`sourceAfterEdit` in `routes/wines.ts`), so typing
 * a disgorgement date cannot quietly restamp 95 imported windows as the
 * owner's own judgement. Without that rule this screen would have needed a
 * route of its own. The photograph is the ordinary upload on the wine's own
 * photographs route, for the same reason; all the list route gained is a
 * count, which is the one thing about photographs it could not work out for
 * itself.
 */

/** Where a wine is stood, for the purpose of walking to it. */
type StopKind = "shelf" | "unplaced" | "away" | "gone";

/** The rank each kind of stop is walked in: the shelves first, because they
 *  are the only ones that are actually a place to stand. */
const STOP_ORDER: Readonly<Record<StopKind, number>> = {
  shelf: 0,
  unplaced: 1,
  away: 2,
  gone: 3,
};

type Stop = {
  readonly key: string;
  readonly kind: StopKind;
  readonly storage: string;
  readonly shelf: string;
  readonly wines: WineListRow[];
};

/**
 * Which stop a wine is walked to.
 *
 * The first shelf it has bottles on, and not all of them: one bottle is enough
 * to read a back label from, and a wine standing in two cabinets would
 * otherwise be asked for the same date twice on the same walk.
 *
 * The three fallbacks are the states a bottle can be in that is not on a
 * shelf. A wine that is out at a dinner is still owned and still has a label
 * to read, but not tonight; one whose shelf was removed under it is findable
 * but not by this list; and a wine drunk dry has no bottle at all, which is
 * the one case where the answer may simply never be written down.
 */
function stopFor(wine: WineListRow): Pick<Stop, "kind" | "storage" | "shelf"> {
  const standing = wine.standing[0];
  if (standing !== undefined) {
    return standing.storageName === null || standing.shelfName === null
      ? { kind: "unplaced", storage: "", shelf: "" }
      : { kind: "shelf", storage: standing.storageName, shelf: standing.shelfName };
  }
  if (wine.away > 0) return { kind: "away", storage: "", shelf: "" };
  return { kind: "gone", storage: "", shelf: "" };
}

/**
 * The route: the wines grouped by the shelf they are on, in the order somebody
 * carrying a phone would meet them.
 *
 * Cabinets and shelves are sorted by name rather than by the order they were
 * set up in, because a list row carries the names and not the cabinet's own
 * ordering. Within a shelf the wines come by number, which is the order they
 * are physically labelled in and therefore the order the eye finds them.
 */
function walkThrough(wines: readonly WineListRow[]): Stop[] {
  const stops = new Map<string, Stop>();
  for (const wine of wines) {
    const where = stopFor(wine);
    // Serialised rather than joined with a separator: a cabinet is called
    // whatever its owner calls it, and no character is safe to assume out of
    // a name.
    const key = JSON.stringify([where.kind, where.storage, where.shelf]);
    const stop = stops.get(key) ?? { key, ...where, wines: [] };
    stop.wines.push(wine);
    stops.set(key, stop);
  }
  const walk = [...stops.values()];
  for (const stop of walk) stop.wines.sort((a, b) => a.number - b.number);
  return walk.sort(
    (a, b) =>
      STOP_ORDER[a.kind] - STOP_ORDER[b.kind] ||
      a.storage.localeCompare(b.storage, "sv") ||
      a.shelf.localeCompare(b.shelf, "sv"),
  );
}

type State =
  | { status: "loading" }
  | { status: "ready"; wines: WineListRow[] }
  | { status: "failed" };

export function Gaps() {
  const { t } = useTranslation();
  const { cellarId = "" } = useParams();
  const mayWrite = useMayWrite();
  const [state, setState] = useState<State>({ status: "loading" });
  /**
   * The wines filled in during this visit, kept on the list after they stop
   * qualifying for it.
   *
   * A row that vanished the moment it was saved would take the next row out
   * from under the thumb that was about to reach it, and would leave no trace
   * that the bottle in your hand had already been dealt with — on a walk
   * through a cabinet that is the difference between finishing and starting
   * over. They stay, marked done, until the screen is left.
   */
  const [filledIn, setFilledIn] = useState<ReadonlySet<string>>(new Set());
  /**
   * What this walk is for. The three anchors, which is what the screen showed
   * before there was a choice — see `ANCHORS`, which holds the default so that
   * it is written down once rather than here and in the domain both.
   *
   * Not remembered between visits, unlike the wine list's narrowing. A filter
   * on a list is a way of looking at something that is still there when you
   * come back; this is an errand, and an errand that resumed itself a week
   * later would answer a question nobody had asked yet.
   */
  const [looking, setLooking] = useState<ReadonlySet<MissingFact>>(new Set(ANCHORS));

  const load = useCallback(() => {
    void fetchWines(cellarId)
      .then((wines) => setState({ status: "ready", wines }))
      .catch(() => setState({ status: "failed" }));
  }, [cellarId]);

  useReload(load);

  const wines = state.status === "ready" ? state.wines : [];
  /**
   * How many wines owe each thing, counted off the cellar in front of you and
   * printed on the choice itself.
   *
   * *Degorgering (32)* is what says the errand is an evening's work before
   * anybody sets off, and it is the same habit the decisions behind this
   * screen were made with: the measurement is the argument. Counted over every
   * wine rather than over what is listed, because the question the number
   * answers is what ticking that box would summon.
   */
  const owed = useMemo(() => {
    const counts = new Map<MissingFact, number>();
    for (const wine of wines) {
      // What can still be read, not what is missing: a label already found
      // silent on a fact does not make that box summon the bottle again.
      for (const fact of stillToRead(wine)) {
        counts.set(fact, (counts.get(fact) ?? 0) + 1);
      }
    }
    return counts;
  }, [wines]);

  /**
   * `10.24` — whether the wines already read and found silent on what is
   * being walked for are shown. Off by default, because leaving them off is the
   * mark's whole purpose; on, because a mark made in a dark cellar has to be as
   * easy to undo as it was to make, and the only place to undo it is its row.
   */
  const [showSilent, setShowSilent] = useState(false);
  const silentFor = (wine: WineListRow): boolean =>
    !walkedFor(wine, looking) &&
    whatIsMissing(wine).some(
      (fact) =>
        looking.has(fact) && isLabelFact(fact) && wine.labelSilentOn.includes(fact),
    );
  const silentCount = wines.filter(silentFor).length;

  /**
   * Whether photographs can be walked for at all.
   *
   * Null from every row means the API did not say — an older one that predates
   * the count — and the choice is then not offered rather than offered and
   * answering with the whole cellar. The contract carries the distinction; see
   * `photoCount` on `wineListRowSchema`.
   */
  const photosKnown = wines.some((wine) => typeof wine.photoCount === "number");
  const offered = MISSING_FACTS.filter((fact) => fact !== "photo" || photosKnown);

  const listed = useMemo(
    () =>
      wines.filter(
        (wine) =>
          walkedFor(wine, looking) ||
          filledIn.has(wine.id) ||
          (showSilent && silentFor(wine)),
      ),
    // `silentFor` is rebuilt every render but reads only `looking`, which is
    // already here, so the list is recomputed exactly when it can change.
    [wines, looking, filledIn, showSilent],
  );
  const walk = useMemo(() => walkThrough(listed), [listed]);
  const left = listed.filter((wine) => walkedFor(wine, looking)).length;
  const done = listed.length - left;
  const chosenDefault =
    looking.size === ANCHORS.length && ANCHORS.every((fact) => looking.has(fact));

  /**
   * What one row saves. The answer comes back as the wine as it now stands, so
   * the row redraws from the record rather than from what was typed into it —
   * the same reason `api.ts` parses every response instead of assuming.
   */
  const save = async (wine: WineListRow, values: UpdateWineRequest): Promise<void> => {
    const saved: WineSummary = await updateWine(cellarId, wine.id, values);
    setFilledIn((current) => new Set(current).add(wine.id));
    setState((current) =>
      current.status === "ready"
        ? {
            status: "ready",
            wines: current.wines.map((row) =>
              // The bottles are not this route's business and are not in its
              // answer: where they stand, how many are out and what anybody
              // scored the wine stay as the list sent them.
              row.id === wine.id ? { ...row, ...saved } : row,
            ),
          }
        : current,
    );
  };

  /**
   * What one row's photograph does to the list.
   *
   * The count is raised here rather than by refetching the cellar: the upload
   * answered, so the wine has a photograph, and asking 122 wines again to
   * learn one number would take the next row out from under the thumb that is
   * already reaching for it. The row stays on the list, marked, exactly as a
   * saved field does.
   */
  /**
   * What marking a label silent does to the list: the row stays for the visit,
   * as a saved field's does, saying what was marked and offering it back.
   */
  const silenced = (wine: WineListRow, facts: LabelFact[]): void => {
    setFilledIn((current) => new Set(current).add(wine.id));
    setState((current) =>
      current.status === "ready"
        ? {
            status: "ready",
            wines: current.wines.map((row) =>
              row.id === wine.id ? { ...row, labelSilentOn: facts } : row,
            ),
          }
        : current,
    );
  };

  const photographed = (wine: WineListRow): void => {
    setFilledIn((current) => new Set(current).add(wine.id));
    setState((current) =>
      current.status === "ready"
        ? {
            status: "ready",
            wines: current.wines.map((row) =>
              row.id === wine.id
                ? { ...row, photoCount: (row.photoCount ?? 0) + 1 }
                : row,
            ),
          }
        : current,
    );
  };

  // A guest has no link here — the row of sections stopped drawing it at `9.2`
  // — but a typed address still arrives, and what is behind it is a list of
  // the owner's unfinished work.
  if (mayWrite === false) {
    return (
      <section>
        <CellarHeading cellarId={cellarId} current="gaps" />
        <OwnersOnly cellarId={cellarId} />
      </section>
    );
  }

  return (
    <section>
      <CellarHeading cellarId={cellarId} current="gaps" />
      <p className="text-muted mt-2 max-w-prose text-sm">{t("gaps.intro")}</p>

      {/* The other walk past the cabinets, and the way to it — *Inventering*
          left the row of sections on 2026-09-18 and is reached from here, the
          way *Till serveringen* is reached from *Att öppna*. The two errands
          are one walk with two questions, so the page you are on when you
          think of the other one is this one. Unconditional, and above the list
          rather than under it: it is a walk you set off on, not a conclusion
          you reach after filling in forty fields. */}
      <p className="text-muted mt-2 max-w-prose text-sm">
        {t("gaps.toStocktake")}{" "}
        <Link
          to={`/kallare/${cellarId}/inventering`}
          className="text-accent underline-offset-4 hover:underline"
        >
          {t("gaps.toStocktakeHint")}
        </Link>
      </p>

      {/* What the walk is for. A row of ticks rather than the wine list's row
          of selects, because these are not narrowings of one list — each one
          is a reason to pick a bottle up, and they add rather than cut. Set
          out in the same grid of equal columns the filter bar uses, which is
          two columns on a phone and one line on a desktop, so the labels land
          at one position instead of six.

          Drawn only when the wines have arrived: a set of choices with no
          counts on them is a set of choices that cannot be judged, and this
          bar's whole claim is that the number is on the label. */}
      {state.status === "ready" && (
        <fieldset className="mt-6">
          <legend className="text-muted text-sm">{t("gaps.lookingFor")}</legend>
          <div className="mt-2 grid grid-cols-[repeat(auto-fit,minmax(9rem,1fr))] gap-x-4 gap-y-2">
            {offered.map((fact) => {
              const ticked = looking.has(fact);
              return (
                <label
                  key={fact}
                  className={`flex items-center gap-2 text-sm ${
                    ticked ? "text-accent" : "text-muted"
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={ticked}
                    onChange={() =>
                      setLooking((current) => {
                        const next = new Set(current);
                        if (!next.delete(fact)) next.add(fact);
                        return next;
                      })
                    }
                    className="accent-accent"
                  />
                  {/* The count is the wine's own word for it, in parentheses
                      and not in a badge: it is a qualification of the label
                      rather than a second thing on the row. */}
                  <span>
                    {t(`gaps.fact.${fact}`)}{" "}
                    <span className="text-muted">({owed.get(fact) ?? 0})</span>
                  </span>
                </label>
              );
            })}
          </div>
          {/* The way back, shown only when there is somewhere to go back to —
              the same rule the wine list's *rensa* follows. */}
          {!chosenDefault && (
            <button
              type="button"
              className="text-accent mt-3 text-sm underline-offset-4 hover:underline"
              onClick={() => setLooking(new Set(ANCHORS))}
            >
              {t("gaps.backToAnchors")}
            </button>
          )}
        </fieldset>
      )}

      {/* `10.24`: the wines already read and found silent, counted rather than
          vanished, and one tap from being shown with their marks undoable.
          Under the choices and above the list, because it answers *why is
          this box's number smaller than the cellar's gap* before the list does,
          and it has to be there when the list is empty for exactly that
          reason. */}
      {state.status === "ready" && (silentCount > 0 || showSilent) && (
        <p className="text-muted mt-4 max-w-prose text-sm">
          {t("gaps.silent.count", { count: silentCount })}{" "}
          <button
            type="button"
            className="text-accent underline-offset-4 hover:underline"
            onClick={() => setShowSilent((current) => !current)}
          >
            {showSilent ? t("gaps.silent.hide") : t("gaps.silent.show")}
          </button>
        </p>
      )}

      {state.status === "loading" && (
        <p className="text-muted mt-6 text-sm">{t("loading")}</p>
      )}
      {state.status === "failed" && (
        <p role="alert" className="text-past mt-6 text-sm">
          {t("wines.loadFailed")}
        </p>
      )}

      {state.status === "ready" &&
        (listed.length === 0 ? (
          /* Three different nothings, and they must not share a sentence. No
             box ticked is the reader's own doing and says so; an empty list
             under the anchors is the cellar knowing everything it can; an
             empty list under a chosen set is a finished errand and not a
             finished cellar. */
          <p className="text-muted mt-8 max-w-prose text-sm">
            {looking.size === 0
              ? t("gaps.nothingChosen")
              : chosenDefault
                ? t("gaps.empty")
                : t("gaps.emptyChosen")}
          </p>
        ) : (
          <>
            {/* Joined with a comma rather than a middle dot, which
                design-direction.md lists among the things this interface is
                not. The dot survives in the shelf headings below, where it
                separates a cabinet from a shelf and not two facts. */}
            <p className="text-muted mt-6 text-sm">
              {t("gaps.left", { count: left })}
              {done > 0 && <>, {t("gaps.done", { count: done })}</>}
            </p>

            {walk.map((stop) => (
              <section key={stop.key} className="mt-8">
                <h2 className="font-display text-ink text-base">
                  {stop.kind === "shelf"
                    ? `${stop.storage} · ${stop.shelf}`
                    : t(`gaps.stop.${stop.kind}`)}
                </h2>
                {stop.kind === "gone" && (
                  <p className="text-muted mt-1 max-w-prose text-sm">
                    {t("gaps.stop.goneHint")}
                  </p>
                )}
                <ul className="border-line mt-3 border-t">
                  {stop.wines.map((wine) => (
                    <GapRow
                      key={wine.id}
                      cellarId={cellarId}
                      wine={wine}
                      looking={looking}
                      onSave={(values) => save(wine, values)}
                      onPhotographed={() => photographed(wine)}
                      onSilenced={(facts) => silenced(wine, facts)}
                    />
                  ))}
                </ul>
              </section>
            ))}
          </>
        ))}
    </section>
  );
}

const asText = (value: number | null): string => (value === null ? "" : String(value));

/** A decimal back into the field it was typed into, with the Swedish comma.
 *  Not `asText`, which is right for a year and would hand back `9.5` to
 *  somebody who typed `9,5`; and not the other way round either, because
 *  `toLocaleString` turns a vintage into `2 019`. Undefined is one of the
 *  shapes `alcoholPercent` still arrives in, its contract being lenient for
 *  the release it went out in. */
const asDecimalText = (value: number | null | undefined): string =>
  value === null || value === undefined ? "" : formatDecimal(value);

/** A year as the contract will accept it, or nothing. Checked before the
 *  request rather than after it, so a slipped digit is refused by the control
 *  it was typed into instead of by a failure four seconds later. */
function soundYear(text: string): boolean {
  if (text.trim() === "") return true;
  const year = Number(text);
  return Number.isInteger(year) && year >= 1800 && year <= 2200;
}

/** The contract's own bounds on a dosage, checked at the control rather than
 *  four seconds later in a 400. The comma rule behind it is in `decimal.ts`,
 *  which the registration form reads too. */
const soundDosage = (text: string): boolean => soundDecimal(text, 0, 100);

/** And the alcohol's, which are 0 and 25 — drawn where the domain ends rather
 *  than where `Decimal(4, 2)` does, so that `135` typed for `13,5` is caught.
 *  The same two numbers the registration form checks, from the same rule. */
const soundAlcohol = (text: string): boolean => soundDecimal(text, 0, 25);

/**
 * One wine on the walk, and the fields it still owes the cellar.
 *
 * It asks for what was missing when the row was drawn, plus anything the
 * answers themselves make missing — ticking *årgångslöst* on a Champagne is
 * what turns the base vintage into a question — and it keeps asking for them
 * after they are answered, so the row reads back as what was just written
 * rather than collapsing to a tick.
 *
 * The dosage and the alcohol are the exceptions and are offered whatever
 * brought the row here — the dosage to sparkling wine, the alcohol to every
 * wine, because every bottle states one. Both are on the back label, both
 * move the answer, and neither is ever a reason to walk to a cabinet: see
 * `WineGap`. Offered and not summoned is the whole distinction; a field that
 * put all 122 wines on this list would empty the list of meaning.
 */
function GapRow({
  cellarId,
  wine,
  looking,
  onSave,
  onPhotographed,
  onSilenced,
}: {
  cellarId: string;
  wine: WineListRow;
  looking: ReadonlySet<MissingFact>;
  onSave: (values: UpdateWineRequest) => Promise<unknown>;
  onPhotographed: () => void;
  onSilenced: (facts: LabelFact[]) => void;
}) {
  const { t } = useTranslation();
  const fieldId = useId();
  const [silentOn, setSilentOn] = useState<readonly LabelFact[]>(wine.labelSilentOn);
  const [silencing, setSilencing] = useState(false);
  const [silenceFailed, setSilenceFailed] = useState(false);

  const [vintage, setVintage] = useState(asText(wine.vintage));
  const [isNonVintage, setIsNonVintage] = useState(wine.isNonVintage);
  const [baseVintage, setBaseVintage] = useState(asText(wine.baseVintage));
  const [disgorgedOn, setDisgorgedOn] = useState(wine.disgorgedOn ?? "");
  // A decimal, and therefore written with the comma it was typed with — the
  // registration form does the same since the alcohol field arrived, and the
  // two must not show one stored 9,5 as two different numbers.
  const [dosage, setDosage] = useState(asDecimalText(wine.dosageGramsPerLitre));
  // Offered to every row on the list since 10.8, and the reason is the whole
  // reason this screen exists: the number is on the back label, and this is
  // the one moment somebody is holding the bottle.
  const [alcohol, setAlcohol] = useState(asDecimalText(wine.alcoholPercent));
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  /**
   * How many photographs this wine has, as this row knows it.
   *
   * Held on the row as well as on the list because the row is what raises it,
   * and because null and nought must keep meaning different things all the way
   * down: null is an API that did not say, and the camera is then not offered
   * at all rather than offered on a wine that may well already have six.
   */
  const [photographs, setPhotographs] = useState(wine.photoCount ?? null);
  const camera = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [cameraFailed, setCameraFailed] = useState<string | null>(null);

  /** What this row was put on the list for, read once. Held rather than
   *  recomputed, so a field does not disappear the instant it is filled in. */
  const [asked] = useState<readonly WineGap[]>(() => gapsInWhatIsKnown(wine));

  const bubbles = sparkles(wine.type);
  const asTyped = {
    type: wine.type,
    vintage: isNonVintage || vintage.trim() === "" ? null : Number(vintage),
    isNonVintage,
    baseVintage: baseVintage.trim() === "" ? null : Number(baseVintage),
    disgorgedOn: disgorgedOn === "" ? null : disgorgedOn,
  };
  const shows = (gap: WineGap): boolean =>
    asked.includes(gap) || gapsInWhatIsKnown(asTyped).includes(gap);

  /**
   * What this row still owes *the errand*, which is what decides whether it
   * reads as done.
   *
   * Measured against what was ticked and not against everything a wine could
   * lack: a row summoned for its alcohol and given one is finished, and a
   * photograph nobody asked for on this walk is not what makes it unfinished.
   *
   * The two decimals are read as written-or-not rather than as numbers —
   * whether the field holds anything is the whole question here, and whether
   * what it holds is a sound number is the save button's business.
   */
  const written = (text: string): number | null => (text.trim() === "" ? null : 0);
  const remaining = whatIsMissing({
    ...asTyped,
    dosageGramsPerLitre: written(dosage),
    alcoholPercent: written(alcohol),
    photoCount: photographs,
  }).filter((fact) => looking.has(fact));

  /**
   * `10.24`: of what this walk is for and the record still lacks, what the
   * label can be said to be silent on, and what it already has been. Read off
   * the fields as typed, so a value half-entered is not also marked absent.
   */
  const unread = remaining.filter(
    (fact): fact is LabelFact => isLabelFact(fact) && !silentOn.includes(fact),
  );
  const silentHere = remaining.filter(
    (fact): fact is LabelFact => isLabelFact(fact) && silentOn.includes(fact),
  );

  const markSilent = (next: readonly LabelFact[]) => {
    setSilencing(true);
    setSilenceFailed(false);
    void setLabelSilence(cellarId, wine.id, next)
      .then((stored) => {
        setSilentOn(stored);
        onSilenced(stored);
      })
      .catch(() => setSilenceFailed(true))
      .finally(() => setSilencing(false));
  };

  const sound =
    soundYear(vintage) &&
    soundYear(baseVintage) &&
    soundDosage(dosage) &&
    soundAlcohol(alcohol) &&
    !busy;
  // A year typed and then struck out by the flag is not a change waiting to be
  // saved: the wine has no year either way, and a save button left standing
  // after a save that worked is the row saying it did not.
  const changed =
    (isNonVintage ? "" : vintage.trim()) !== asText(wine.vintage) ||
    isNonVintage !== wine.isNonVintage ||
    baseVintage.trim() !== asText(wine.baseVintage) ||
    disgorgedOn !== (wine.disgorgedOn ?? "") ||
    dosage.trim() !== asDecimalText(wine.dosageGramsPerLitre) ||
    alcohol.trim() !== asDecimalText(wine.alcoholPercent);

  const submit = () => {
    if (!sound || !changed) return;
    setBusy(true);
    setFailed(false);
    void onSave({
      // Everything that is not being filled in goes back exactly as it came,
      // which is what makes this the ordinary wine update rather than a route
      // of its own. The years keep their provenance because they have not
      // changed — see the note at the top of this file.
      name: wine.name,
      type: wine.type,
      countryCode: wine.countryCode,
      producer: wine.producer,
      region: wine.region,
      style: wine.style,
      grapes: [...wine.grapes],
      drinkFrom: wine.drinkFrom,
      peakFrom: wine.peakFrom,
      peakTo: wine.peakTo,
      drinkBy: wine.drinkBy,
      vintage: asTyped.vintage,
      isNonVintage,
      // The Champagne fields belong to Champagne. A still wine cannot arrive
      // here carrying them, and one whose type was corrected to a still wine
      // elsewhere should not keep them.
      baseVintage: bubbles ? asTyped.baseVintage : null,
      disgorgedOn: bubbles ? asTyped.disgorgedOn : null,
      dosageGramsPerLitre: bubbles && dosage.trim() !== "" ? decimalOf(dosage) : null,
      // Not conditioned on the type, unlike the three above: the alcohol is a
      // fact about every bottle rather than about Champagne, which is the same
      // sentence that kept it out of the Champagne fields on the form. An
      // emptied field clears it, here as there.
      alcoholPercent: alcohol.trim() === "" ? null : decimalOf(alcohol),
    })
      .catch(() => setFailed(true))
      .finally(() => setBusy(false));
  };

  /**
   * The photograph, taken where the bottle is.
   *
   * The ordinary upload on the wine's own photographs route, resized in the
   * browser first exactly as the wine's page does it — this row knows nothing
   * about photographs that `WinePhotos` does not, and deliberately reuses the
   * two functions rather than growing a second way to send one.
   *
   * It designates unconditionally, which is safe because the button is only
   * offered to a wine with none: the first photograph of a wine is designated
   * whatever a caller asks for, since a wine with photographs and none of them
   * designated has nothing to put on its page or in the report.
   *
   * The three failures are told apart for the reason they are told apart on
   * the wine's page: *try again* is useless advice for two of them. The one
   * this screen is likeliest to meet is the iPhone's HEIC arriving whole.
   */
  async function photograph(file: File): Promise<void> {
    setUploading(true);
    setCameraFailed(null);
    try {
      const sizes = await resizeForUpload(file);
      await uploadWinePhoto(cellarId, wine.id, { ...sizes, designate: true });
      setPhotographs((current) => (current ?? 0) + 1);
      onPhotographed();
    } catch (cause) {
      if (cause instanceof UnreadableImage) setCameraFailed(t("photos.unreadable"));
      else if (cause instanceof ApiError && cause.code === "photo_store_unavailable")
        setCameraFailed(t("photos.unavailable"));
      else if (cause instanceof ApiError && cause.code === "photo_too_large")
        setCameraFailed(t("photos.tooLarge"));
      else setCameraFailed(t("photos.failed"));
    } finally {
      setUploading(false);
      if (camera.current !== null) camera.current.value = "";
    }
  }

  return (
    <li className="border-line border-b py-4">
      <form
        className="grid grid-cols-[2.75rem_1fr] gap-x-4"
        onSubmit={(event) => {
          event.preventDefault();
          submit();
        }}
      >
        <div className="font-display text-muted pt-0.5 text-right text-lg">
          {wine.number}
        </div>

        <div className="min-w-0">
          <div className="flex items-baseline justify-between gap-4">
            <h3 className="font-display truncate text-lg leading-tight">{wine.name}</h3>
            {/* A tick rather than a word would be state carried by one mark;
                this says what happened, and stays said. */}
            {remaining.length === 0 && !changed && silentHere.length === 0 && (
              <span className="text-muted shrink-0 text-sm">{t("gaps.filledIn")}</span>
            )}
          </div>
          <p className="text-muted mt-0.5 truncate text-sm">
            {wine.producer}, {t(`wineType.${wine.type}`)}
          </p>

          <div className="mt-3 grid gap-3 sm:grid-cols-3">
            {shows("vintage") && (
              <div>
                <label className={fieldLabel} htmlFor={`${fieldId}-vintage`}>
                  {t("wines.vintage")}
                </label>
                <input
                  id={`${fieldId}-vintage`}
                  inputMode="numeric"
                  value={isNonVintage ? "" : vintage}
                  disabled={isNonVintage}
                  aria-invalid={!soundYear(vintage)}
                  onChange={(event) => setVintage(event.target.value)}
                  placeholder={isNonVintage ? t("wines.nvShort") : ""}
                  className={`${control} ${resting} mt-1 disabled:opacity-40`}
                />
                {/* The answer to *this wine has no year* is not a year, and
                    two of the eleven are named `N.V.` without anything in the
                    record saying so. The flag is how that gets said. */}
                <label className="mt-2 flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={isNonVintage}
                    onChange={(event) => setIsNonVintage(event.target.checked)}
                    className="accent-accent"
                  />
                  {t("wines.nonVintage")}
                </label>
              </div>
            )}

            {shows("baseVintage") && (
              <div>
                <label className={fieldLabel} htmlFor={`${fieldId}-base`}>
                  {t("wines.baseVintage")}
                </label>
                <input
                  id={`${fieldId}-base`}
                  inputMode="numeric"
                  value={baseVintage}
                  aria-invalid={!soundYear(baseVintage)}
                  onChange={(event) => setBaseVintage(event.target.value)}
                  className={`${control} ${resting} mt-1`}
                />
              </div>
            )}

            {shows("disgorgement") && (
              <div>
                <label className={fieldLabel} htmlFor={`${fieldId}-disgorged`}>
                  {t("wines.disgorgedOn")}
                </label>
                <input
                  id={`${fieldId}-disgorged`}
                  type="date"
                  value={disgorgedOn}
                  onChange={(event) => setDisgorgedOn(event.target.value)}
                  className={`${control} ${resting} mt-1`}
                />
              </div>
            )}

            {/* Last of the fields and offered to every row, because it is the
                one here that is not about why the wine was summoned. The
                bottle is in somebody's hand for the sake of the questions
                above; this is what else the label says while it is. */}
            <div>
              <label className={fieldLabel} htmlFor={`${fieldId}-alcohol`}>
                {t("wines.alcohol")}
              </label>
              <input
                id={`${fieldId}-alcohol`}
                inputMode="decimal"
                value={alcohol}
                aria-invalid={!soundAlcohol(alcohol)}
                onChange={(event) => setAlcohol(event.target.value)}
                placeholder="13,5"
                className={`${control} ${resting} mt-1`}
              />
              {/* Said at the field, as the registration form says it. This
                  screen disabled its save in silence until 10.6 made that
                  silence obvious on the other form; the same answer here. */}
              {!soundAlcohol(alcohol) && (
                <p role="alert" className="text-past mt-1 text-sm">
                  {t("wines.alcoholOutOfRange")}
                </p>
              )}
            </div>

            {bubbles && (
              <div>
                <label className={fieldLabel} htmlFor={`${fieldId}-dosage`}>
                  {t("wines.dosage")}
                </label>
                <input
                  id={`${fieldId}-dosage`}
                  inputMode="decimal"
                  value={dosage}
                  aria-invalid={!soundDosage(dosage)}
                  onChange={(event) => setDosage(event.target.value)}
                  className={`${control} ${resting} mt-1`}
                />
                {!soundDosage(dosage) && (
                  <p role="alert" className="text-past mt-1 text-sm">
                    {t("wines.dosageOutOfRange")}
                  </p>
                )}
              </div>
            )}

            {/* The camera, offered on the same terms as the alcohol and for a
                stronger version of the same reason: every other field on this
                row can be filled in later from a note, and a photograph of a
                bottle cannot be taken anywhere except in front of the bottle.
                Offered only where there is none — a wine already photographed
                is not what this walk is for — and never where the count did
                not arrive, because then nobody knows whether there is one. */}
            {photographs === 0 && (
              <div>
                <span className={fieldLabel}>{t("gaps.photo.label")}</span>
                <input
                  ref={camera}
                  type="file"
                  accept="image/*"
                  // What tells a phone to open the camera rather than the
                  // photo library. Ignored on a desktop, where the file picker
                  // is the right answer anyway.
                  capture="environment"
                  className="hidden"
                  onChange={(event) => {
                    const file = event.target.files?.[0];
                    if (file !== undefined) void photograph(file);
                  }}
                />
                {/* A button, shaped like every other button in this
                    application, and deliberately **not** full width.

                    Both wrong answers were tried on the way here. Stretched
                    across its cell it was a brass slab repeated down
                    thirty-five rows and the loudest thing on a page whose
                    subject is the dates. Drawn as a field instead it went
                    quiet by pretending to be something it is not — a box the
                    width of an input, with a word sitting where a typed value
                    sits, which reads as a text field and was read as one.

                    A control must look like what it does. So it stays an
                    `actionButton` and simply hugs its label: the quiet comes
                    from its size, which is what `controls.tsx` says quiet is
                    carried by, and not from taking the button away. */}
                <button
                  type="button"
                  disabled={uploading}
                  onClick={() => camera.current?.click()}
                  className={`${actionButton} mt-1`}
                >
                  {uploading ? t("gaps.photo.uploading") : t("gaps.photo.take")}
                </button>
                {cameraFailed !== null && (
                  <p role="alert" className="text-past mt-1 text-sm">
                    {cameraFailed}
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Shaped like the temperature field on the cabinets: the button is
              there when there is something to save and absent when there is
              not, because a control that is always live invites a tap that
              does nothing. */}
          {(changed || failed) && (
            <div className="mt-3 flex flex-wrap items-center gap-4">
              <button type="submit" disabled={!sound} className={actionButton}>
                {busy ? t("gaps.saving") : t("gaps.save")}
              </button>
              {failed && (
                <span role="alert" className="text-past text-sm">
                  {t("gaps.saveFailed")}
                </span>
              )}
            </div>
          )}

          {/* `10.24`: the label was read and says nothing. Offered only when
              nothing has been typed — a row with a value in it is being
              filled in, not given up on — and only for the facts this walk is
              for, because a label read for its disgorgement date was not read
              for anything else. */}
          {!changed && unread.length > 0 && (
            <div className="mt-3 flex flex-wrap items-center gap-4">
              <button
                type="button"
                disabled={silencing}
                onClick={() => markSilent([...silentOn, ...unread])}
                className={actionButton}
              >
                {t("gaps.silent.mark")}
              </button>
            </div>
          )}
          {silentHere.length > 0 && (
            <p className="text-muted mt-3 text-sm">
              {t("gaps.silent.marked", {
                facts: silentHere.map((fact) => t(`gaps.fact.${fact}`).toLocaleLowerCase("sv")).join(", "), // prettier-ignore
              })}{" "}
              <button
                type="button"
                disabled={silencing}
                className="text-accent underline-offset-4 hover:underline"
                onClick={() =>
                  markSilent(silentOn.filter((fact) => !silentHere.includes(fact)))
                }
              >
                {t("gaps.silent.undo")}
              </button>
            </p>
          )}
          {silenceFailed && (
            <p role="alert" className="text-past mt-2 text-sm">
              {t("gaps.saveFailed")}
            </p>
          )}
        </div>
      </form>
    </li>
  );
}
